import { createDefaultSettings } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/AdvancedSettingsSection';
import { NO_PREDICTIONS_GENERATION_TARGET } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useGenerationTargets';
import { normalizeSourceConfig } from '@/features/datasets/utils/datasetSourceConfig';

/**
 * Mapping helpers from a "Load Previous Generation Configurations" session
 * (the persisted `source_config` of a past run, normalized by
 * `normalizeSourceConfig`) onto the Generate-with-AI flow state shapes.
 */

/** Normalized UI config from a `GET ai-generation/sessions` row; null when unusable. */
export function generationSessionConfig(session) {
    return normalizeSourceConfig(session?.config);
}

/** Advanced-settings state (`createDefaultSettings` shape) from a normalized config. */
export function settingsFromConfig(config) {
    const defaults = createDefaultSettings();
    const settings = config?.settings || {};
    return {
        domain: settings.domain ?? defaults.domain,
        languages: settings.languages?.length ? settings.languages : defaults.languages,
        toneStyles: settings.toneStyles || defaults.toneStyles,
        textLength: settings.textLength || defaults.textLength,
        contentType: settings.contentType || defaults.contentType,
        customGuidelines: settings.customGuidelines ?? defaults.customGuidelines,
    };
}

let prefillRowSeq = 0;
const nextRowId = () => `prefill_${(prefillRowSeq += 1)}`;

/** Multi-label Examples+Labels rows. The stored No-predictions marker maps back to an empty selection. */
export function multiLabelExampleRowsFromConfig(config) {
    return (config?.examples || []).flatMap((example) => {
        const text = (example?.text || '').trim();
        if (!text) return [];
        const labels = (example?.response?.labels || []).filter(
            (label) => label && label !== NO_PREDICTIONS_GENERATION_TARGET
        );
        return [{ id: nextRowId(), text, labels }];
    });
}

/**
 * Summarization / content-generation Input Text + Wanted Output pairs. Content
 * generation stores `response.response`; sessions saved before it had its own
 * example kind used the summarization `response.summary` shape.
 */
export function pairedExampleRowsFromConfig(config) {
    return (config?.examples || []).flatMap((example) => {
        const text = (example?.text || '').trim();
        const summary = (example?.response?.summary || example?.response?.response || '').trim();
        if (!text || !summary) return [];
        return [{ id: nextRowId(), text, summary }];
    });
}

/** NER tagged-example rows; stored entity spans round-trip into the editor. */
export function nerExampleRowsFromConfig(config) {
    return (config?.examples || []).flatMap((example) => {
        const text = (example?.text || '').trim();
        if (!text) return [];
        const entities = Array.isArray(example?.response?.entities) ? example.response.entities : [];
        return [{ text, entities }];
    });
}
